import { getShortLangCode } from "igfd-commons";
import { createTransform } from "redux-persist";
import type { PersistConfig, Transform } from "redux-persist/es/types";
import storage from "redux-persist/lib/storage";

import { DefaultFeatureFlags } from "@dashboard/environment/defaultFeatureFlags";
import { type ReduxState } from "@dashboard/types/reduxState";

import { type RestaurantSelectedAction } from "./restaurantReducer";

// defaults to localStorage for web

export type DashboardReducerAction =
  | RestaurantSelectedAction // we listen on restaurantSelected
  | { type: "SET_ADMIN_FLAG"; userIsAdmin: boolean } // Set the admin flag.
  | {
      type: "SET_FEATURE_FLAG";
      featureFlag: keyof ReduxState["dashboardApp"]["featureFlags"];
      value: boolean;
    }; // Set the value of a feature flag.

const featureFlagPersistTransform = createTransform(
  // Do nothing when serializing to local storage.
  /*inbound: */ (subState: ReduxState["dashboardApp"], key, state) => subState,

  // When deserializing from a persisted state, for feature flags we set the value to the default value if it is not present.
  /* outbound: */ (
    subState: any, // This type is based on the key. If key is "featureFlags", it is typeof DefaultFeatureFlags.
    key,
    state: ReduxState["dashboardApp"]
  ) => {
    // this transform is only for the featureFlags key.
    if (key !== "featureFlags") {
      return subState;
    }
    // return an object where the keys are the same as DefaultFeatureFlags,
    // and the values are the values from the persisted state or the default values.
    return Object.keys(DefaultFeatureFlags).reduce(
      (acc, featureFlag) => ({
        ...acc,
        [featureFlag]:
          // @ts-ignore
          subState[featureFlag] ?? DefaultFeatureFlags[featureFlag],
      }),
      {} as typeof DefaultFeatureFlags
    );
  }
) as Transform<ReduxState["dashboardApp"], ReduxState["dashboardApp"]>;

export const dashboardAppPersistConfig = {
  key: "igfd-dashboard-app",
  storage,
  transforms: [featureFlagPersistTransform],
} as PersistConfig<ReduxState["dashboardApp"]>;

export const dashboardAppReducer: (
  state: ReduxState["dashboardApp"] | undefined,
  action: DashboardReducerAction
) => ReduxState["dashboardApp"] = (
  state: ReduxState["dashboardApp"] = {
    language: "en", // default language (no restaurant selected)
    // we persist this flag in local storage, however it is refreshed on reloading the page.
    // so editing this flag in local storage will not have any effect, since it is refreshed on page load.
    userIsAdmin: false,
    featureFlags: DefaultFeatureFlags,
  },
  action: DashboardReducerAction
) => {
  switch (action.type) {
    case "SET_ADMIN_FLAG":
      return {
        ...state,
        userIsAdmin: action.userIsAdmin,
      };
    case "SET_FEATURE_FLAG":
      // Only admins can change UI feature flags.
      if (!state.userIsAdmin) {
        return state;
      }
      return {
        ...state,
        featureFlags: {
          ...state.featureFlags,
          [action.featureFlag]: action.value,
        },
      };

    case "RESTAURANT_SELECTED":
      // update dashboard editing language to the restaurant's default language
      if (action.payload === null) {
        // the user doesn't have access to any restaurants
        // use the browser language.

        return {
          ...state,
          language: getShortLangCode(),
        };
      }

      const selectedRestaurant = action.payload;
      // get first two chars of the language only. (some restaurant have locale stored for historical reasons)
      const twoCharLangCode = selectedRestaurant.language.substring(0, 2);
      return {
        ...state,
        language: twoCharLangCode,
      };

    default:
      return state;
  }
};
